import { FunctionComponent } from 'preact';
import cn from 'classnames';
import {
  MutableRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'preact/hooks';

import { isMobile } from '../../../../utils/device';

interface VideoPreviewProps {
  url: string;
  playbackStrategy?: 'hover' | 'viewport';
  containerRef: MutableRef<HTMLElement>;
}

export const VideoPreview: FunctionComponent<VideoPreviewProps> = ({
  url,
  playbackStrategy: playbackStrategyProp,
  containerRef,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null!);

  const playbackStrategy =
    (playbackStrategyProp ?? isMobile) ? 'viewport' : 'hover';

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;

    const play = () => {
      video.play();
    };

    const pause = () => {
      video.pause();
    };

    switch (playbackStrategy) {
      case 'hover': {
        container.addEventListener('mouseenter', play);
        container.addEventListener('mouseleave', pause);

        return () => {
          container.removeEventListener('mouseenter', play);
          container.removeEventListener('mouseleave', pause);
        };
      }

      case 'viewport': {
        let timeout: number;

        const observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];

            if (entry.isIntersecting) {
              timeout = window.setTimeout(() => {
                play();
              }, 1000);
            } else {
              window.clearTimeout(timeout);

              pause();
            }
          },
          { threshold: 0.5 }
        );

        observer.observe(container);

        return () => observer.disconnect();
      }

      default: {
        video.autoplay = true;
      }
    }
  }, [playbackStrategy, containerRef]);

  return (
    <video
      ref={videoRef}
      className={cn('vgp-video-preview', {
        'vgp-video-preview-active': isPlaying,
      })}
      src={url}
      controls={false}
      crossOrigin="anonymous"
      loop
      playsInline
      muted
      onPlay={handlePlay}
      onPause={handlePause}
    />
  );
};
